import React, { useState } from 'react';
import OffboardOrganisation, { OffboardOrganisationResult } from '../../services/adapters/offboardOrganisation';

interface Props {
  displayName: string;
  onOffboarded: () => void;
}

type OffboardState =
  | { stage: 'idle' }
  | { stage: 'confirming' }
  | { stage: 'submitting' }
  | { stage: 'done'; result: OffboardOrganisationResult }
  | { stage: 'error'; message: string };

type PanelVariant = 'default' | 'danger';

function PanelWrapper({ variant, children }: Readonly<{ variant: PanelVariant; children: React.ReactNode }>) {
  const borderClass = variant === 'danger' ? 'border-pipstatus-error' : 'border-pipgrey-light';
  return (
    <div className={`border ${borderClass} rounded-md p-4 flex flex-col gap-3`}>
      {children}
    </div>
  );
}

function StatusText({ variant, children }: Readonly<{ variant: PanelVariant; children: React.ReactNode }>) {
  const colourClass = variant === 'danger' ? 'text-pipstatus-error' : 'text-piptext-secondary';
  return <p className={`text-xs ${colourClass}`}>{children}</p>;
}

export default function OffboardOrganisationPanel({ displayName, onOffboarded }: Readonly<Props>) {
  const [state, setState] = useState<OffboardState>({ stage: 'idle' });

  const handleConfirm = async () => {
    setState({ stage: 'submitting' });
    try {
      const result = await new OffboardOrganisation().adaptResponse({ organisationDisplayName: displayName });
      onOffboarded();
      setState({ stage: 'done', result });
    } catch (e) {
      setState({ stage: 'error', message: e instanceof Error ? e.message : 'Offboard failed.' });
    }
  };

  if (state.stage === 'done') {
    const { result } = state;
    return (
      <PanelWrapper variant="default">
        <p className="text-sm font-semibold text-piptext-default">
          {displayName}
          offboarded.
        </p>
        {result.isOffboardingComplete ? (
          <p className="text-xs text-pipstatus-success">All cloud resources cleaned up successfully.</p>
        ) : (
          <div className="flex flex-col gap-1">
            <StatusText variant="danger">
              Offboarded with warnings - manual cleanup may be required. Please check logs.
            </StatusText>
            <ul className="text-xs text-pipstatus-error list-disc list-inside">
              {result.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}
      </PanelWrapper>
    );
  }

  return (
    <PanelWrapper variant="danger">
      <p className="text-sm font-semibold text-piptext-default">Offboard organisation</p>
      <StatusText variant="default">
        Disables the organisation in the database and removes their Cognito SSO config and any SAML
        metadata from S3. The database row is preserved for audit purposes.
      </StatusText>

      {state.stage === 'error' && (
        <StatusText variant="danger">{state.message}</StatusText>
      )}

      {state.stage === 'idle' && (
        <button
          type="button"
          className="self-start button-error-outline"
          onClick={() => setState({ stage: 'confirming' })}
        >
          Offboard…
        </button>
      )}

      {state.stage === 'confirming' && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-pipstatus-error font-medium">
            Offboard
            {' '}
            <span className="font-bold">{displayName}</span>
            ? This is complicated to undo, please be certain.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="button-error-solid"
              onClick={handleConfirm}
            >
              Confirm offboard
            </button>
            <button
              type="button"
              className="button-base button-default"
              onClick={() => setState({ stage: 'idle' })}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {state.stage === 'submitting' && (
        <StatusText variant="default">Offboarding…</StatusText>
      )}
    </PanelWrapper>
  );
}
